import styles from "./SharedUsageNote.module.css";
import { sharedUsageNoteCopy } from "../lib/adoption";
import type { ExtensionType } from "../types/index";

interface SharedUsageNoteProps {
  /** Mirrors `AdoptionEntry.sharedNameUsage` (Contract 7) — renders nothing when falsy. */
  sharedNameUsage?: boolean;
  /** "short": hoverable chip for cards/list rows. "long": always-visible detail-page text. */
  variant?: "short" | "long";
  /** Tailors the copy: a plugin's flag usually comes from a bundled skill's name, not its own. */
  extensionType?: ExtensionType;
}

/** The shared info glyph — identical circle-i used by both variants, per AC. */
function InfoGlyph({ className }: { className: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <line x1="12" y1="8" x2="12" y2="8" />
    </svg>
  );
}

export default function SharedUsageNote({ sharedNameUsage, variant = "short", extensionType }: SharedUsageNoteProps) {
  if (!sharedNameUsage) return null;

  const copy = sharedUsageNoteCopy(variant, extensionType);

  // Always-visible detail-page note, prefixed with the same info icon as the card.
  if (variant === "long") {
    return (
      <div className={styles.longNote}>
        <InfoGlyph className={styles.longNoteIcon} />
        <span>{copy}</span>
      </div>
    );
  }

  // A visible, brand-tinted info affordance with a real hover/focus tooltip bubble —
  // unlike the app's native `title` tooltips, this appears instantly and reliably.
  // A native <button> makes it keyboard-operable (focus + :focus-visible) for free;
  // aria-label carries the accessible name and the visible bubble is decorative to AT.
  return (
    <button type="button" className={styles.chip} aria-label={copy}>
      <InfoGlyph className={styles.chipIcon} />
      <span className={styles.tooltip} aria-hidden="true">
        {copy}
      </span>
    </button>
  );
}
